import {
  Avatar,
  Badge,
  Flex,
  Link as ChakraLink,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ActionFab } from '../../../components/ActionButton'
import { FilterBar, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { StatusBadge, statusOptions } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { formatDateTime, useHasRole, useAppToast } from '../../../helpers'
import { ESupportTicketAssignee, ESupportTicketStatus, ISupportTicket } from '../../../services/Support/interfaces'
import { SupportService } from '../../../services/Support/SupportService'
import { SUPPORT_TICKET_STATUS } from './statuses'
import SupportCreateModal from './SupportCreateModal'

function formatTimeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()

  const minutes = Math.floor(diffMs / 60000)
  const hours = Math.floor(diffMs / 3600000)
  const days = Math.floor(diffMs / 86400000)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  if (minutes < 1) return 'Agora mesmo'
  if (minutes < 60) return `${minutes}min atrás`
  if (hours < 24) {
    const remainMin = minutes % 60
    return remainMin > 0 ? `${hours}h ${remainMin}min atrás` : `${hours}h atrás`
  }
  if (days < 30) return days === 1 ? '1 dia atrás' : `${days} dias atrás`
  if (months < 12) return months === 1 ? '1 mês atrás' : `${months} meses atrás`
  return years === 1 ? '1 ano atrás' : `${years} anos atrás`
}

function formatDuration(createdAt: string): string {
  const now = new Date()
  const date = new Date(createdAt)
  const diffMs = now.getTime() - date.getTime()

  const minutes = Math.floor(diffMs / 60000)
  const hours = Math.floor(diffMs / 3600000)
  const days = Math.floor(diffMs / 86400000)
  const months = Math.floor(days / 30)

  if (minutes < 60) return `Aberto há ${minutes} minuto${minutes !== 1 ? 's' : ''}`
  if (hours < 24) return `Aberto há ${hours} hora${hours !== 1 ? 's' : ''}`
  if (days < 30) return `Aberto há ${days} dia${days !== 1 ? 's' : ''}`
  const remainDays = days % 30
  if (remainDays > 0)
    return `Aberto há ${months} ${months === 1 ? 'mês' : 'meses'} e ${remainDays} dia${remainDays !== 1 ? 's' : ''}`
  return `Aberto há ${months} ${months === 1 ? 'mês' : 'meses'}`
}

/** `page`/`page_size` ficam de fora: são paginação, não filtro. */
const INITIAL_FILTER = {
  search: '',
  status: ESupportTicketStatus.OPEN as string,
  module: '',
  user: ''
}

export default function SupportPage() {
  const toast = useAppToast()
  const navigate = useNavigate()
  const supportService = new SupportService()
  const [supportTickets, setSupportTickets] = useState<ISupportTicket[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [totalPages, setTotalPages] = useState(1)
  const hasRole = useHasRole()
  const permissions = {
    viewSupportRequests: hasRole('view_support_requests')
  }
  const [filter, setFilter] = useState({ ...INITIAL_FILTER, page: 1, page_size: DEFAULT_PAGE_SIZE })
  const isDirty = (Object.keys(INITIAL_FILTER) as (keyof typeof INITIAL_FILTER)[]).some(
    (key) => filter[key] !== INITIAL_FILTER[key]
  )

  // Os três campos de texto já chegam debounced do `FilterSearch`; o select aplica na hora.
  useEffect(() => {
    loadSupportTickets()
  }, [filter])

  // Atualiza lista quando um chamado é criado pela navbar
  useEffect(() => {
    const handler = () => loadSupportTickets()
    window.addEventListener('support-ticket-created', handler)
    return () => window.removeEventListener('support-ticket-created', handler)
  }, [filter])

  const updateFilter = (patch: Partial<typeof filter>) => {
    const isPageOnly = Object.keys(patch).length === 1 && 'page' in patch
    setFilter((prev) => ({ ...prev, ...patch, ...(isPageOnly ? {} : { page: 1 }) }))
  }

  const loadSupportTickets = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await supportService.list(filter)
      setSupportTickets(data.supportTickets)
      setTotalPages(Math.ceil(data.total_filtered / data.page_size))
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os chamados', error)
    } finally {
      setLoading(false)
    }
  }

  // A tabela tem 8 colunas para todo mundo — o papel muda o filtro de requerente, não a
  // coluna. O ternário fazia a linha de vazio/erro/skeleton ocupar 7 de 8 para quem não o tem.
  const colCount = 8

  return (
    <>
      <PageTitle title="Chamados de Suporte" description="Solicitações de suporte abertas pela equipe" />

      <FilterBar onClear={() => updateFilter(INITIAL_FILTER)} isDirty={isDirty}>
        <FilterSearch
          value={filter.search}
          onChange={(search) => updateFilter({ search })}
          placeholder="Buscar por resumo"
        />
        <FilterSelect
          label="Status"
          value={filter.status}
          onChange={(status) => updateFilter({ status })}
          options={statusOptions(SUPPORT_TICKET_STATUS)}
        />
        <FilterSearch
          label="Módulo"
          value={filter.module}
          onChange={(module) => updateFilter({ module })}
          placeholder="Filtrar módulo"
        />
        {permissions.viewSupportRequests && (
          <FilterSearch
            label="Requerente"
            value={filter.user}
            onChange={(user) => updateFilter({ user })}
            placeholder="Filtrar por requerente"
          />
        )}
      </FilterBar>

      <TableContainer mt={2}>
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Requerente</Th>
              <Th>Resumo</Th>
              <Th>Módulo</Th>
              <Th>Responsável</Th>
              <Th>Status</Th>
              <Th>Aberto em</Th>
              <Th>Última interação</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={colCount} />
            ) : loadError ? (
              <TableErrorRow colSpan={colCount} entity="chamados" onRetry={loadSupportTickets} />
            ) : supportTickets.length === 0 ? (
              <TableEmptyRow colSpan={colCount} entity="chamado" />
            ) : (
              supportTickets.map((supportTicket) => (
                <Tr
                  key={supportTicket.id}
                  role="button"
                  tabIndex={0}
                  cursor="pointer"
                  _hover={{ bg: 'gray.50' }}
                  onClick={() => navigate(`/support/${supportTicket.id}`)}
                  onKeyDown={(event) => {
                    // A linha só responde quando ela mesma está focada — o assunto do chamado é
                    // um <Link> focável, e o Espaço nele deve rolar a página, não navegar.
                    if (event.target !== event.currentTarget) return

                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      navigate(`/support/${supportTicket.id}`)
                    }
                  }}
                >
                  <Td>{supportTicket.id}</Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Avatar size="xs" name={supportTicket.user.name} src={supportTicket.user.picture} />
                      {supportTicket.user.name}
                    </Flex>
                  </Td>
                  <Td>
                    <Tooltip label={supportTicket.resume}>
                      <ChakraLink
                        as={Link}
                        to={`/support/${supportTicket.id}`}
                        color="blue.600"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Text noOfLines={1} maxW="250px">
                          {supportTicket.resume}
                        </Text>
                      </ChakraLink>
                    </Tooltip>
                  </Td>
                  <Td>
                    <Text noOfLines={1} maxW="150px">
                      {supportTicket.module}
                    </Text>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={
                        supportTicket.current_assignee === ESupportTicketAssignee.SUPPORT ? 'cyan' : 'purple'
                      }
                      variant="subtle"
                    >
                      {supportTicket.current_assignee === ESupportTicketAssignee.REQUESTER ? 'Solicitante' : 'Suporte'}
                    </Badge>
                  </Td>
                  <Td>
                    <StatusBadge status={supportTicket.status} statuses={SUPPORT_TICKET_STATUS} />
                  </Td>
                  <Td whiteSpace="nowrap">{formatDateTime(supportTicket.created_at)}</Td>
                  <Td whiteSpace="nowrap">
                    <Tooltip label={formatDuration(supportTicket.created_at)}>
                      <Text>{formatTimeAgo(supportTicket.updated_at)}</Text>
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={filter.page}
        totalPages={totalPages}
        changePage={(page: number) => {
          updateFilter({ page })
        }}
        pageSize={filter.page_size}
        onPageSizeChange={(size) => {
          setFilter({ ...filter, page_size: size, page: 1 })
        }}
      />

      <SupportCreateModal
        isOpen={isCreateOpen}
        onClose={() => {
          loadSupportTickets()
          setIsCreateOpen(false)
        }}
      />

      <ActionFab label="Novo chamado" onClick={() => setIsCreateOpen(true)} />
    </>
  )
}
